import React from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import type { TFunction } from "i18next";

import {
  clearHealthPromptDismiss,
  dismissHealthPromptForDay,
  wasHealthPromptDismissedToday,
} from "./healthPromptDismiss";
import type { Appointment } from "../services/appointmentService";

type PromptFillParams = {
  canView: boolean;
  appointment: Appointment;
  notAvailable: string;
  t: TFunction;
  onFill: (appointment: Appointment) => void;
};

const appointmentNeedsHealthRecord = (appointment: Appointment): boolean => {
  if (typeof appointment.needs_health_record === "boolean") {
    return appointment.needs_health_record;
  }

  return (appointment.client?.health_records_count ?? 0) === 0;
};

const appointmentNeedsConsent = (appointment: Appointment): boolean =>
  Boolean(appointment.needs_consent);

/**
 * After "Chegou", offer to fill a missing health record or pending consent (clinical roles).
 * Uses `needs_health_record` / `needs_consent` when present (GAP-F2-052 · GAP-F2-112).
 * "Depois" dismisses for client+day; toast offers "Perguntar agora" to undo.
 */
export async function promptFillHealthRecordIfMissing(
  params: PromptFillParams
): Promise<void> {
  if (!params.canView) {
    return;
  }

  const { appointment } = params;
  const needsHealth = appointmentNeedsHealthRecord(appointment);
  const needsConsent = appointmentNeedsConsent(appointment);

  if (!needsHealth && !needsConsent) {
    return;
  }

  if (!appointment.client_id) {
    return;
  }

  const dayKey = appointment.start_datetime.slice(0, 10);
  if (wasHealthPromptDismissedToday(appointment.client_id, dayKey)) {
    return;
  }

  const clientName = appointment.client?.name ?? params.notAvailable;
  const serviceName = appointment.service?.name;

  let title = params.t("studio.agenda.health.promptTitle");
  let text = serviceName
    ? params.t("studio.agenda.health.promptBodyService", {
        name: clientName,
        service: serviceName,
      })
    : params.t("studio.agenda.health.promptBody", { name: clientName });
  let confirmText = params.t("studio.agenda.health.promptFill");

  if (!needsHealth && needsConsent) {
    title = params.t("studio.agenda.health.consentPromptTitle");
    text = params.t("studio.agenda.health.consentPromptBody", { name: clientName });
    confirmText = params.t("studio.agenda.health.consentPromptFill");
  } else if (needsHealth && needsConsent) {
    title = params.t("studio.agenda.health.preattendPromptTitle");
    text = params.t("studio.agenda.health.preattendPromptBody", { name: clientName });
    confirmText = params.t("studio.agenda.health.promptFill");
  }

  const confirmation = await Swal.fire({
    title,
    text,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: params.t("studio.agenda.health.promptLater"),
    confirmButtonColor: "#0ab39c",
    cancelButtonColor: "#878a99",
    reverseButtons: true,
  });

  if (confirmation.isConfirmed) {
    params.onFill(appointment);
    return;
  }

  if (confirmation.dismiss !== Swal.DismissReason.cancel) {
    return;
  }

  const clientId = appointment.client_id;
  dismissHealthPromptForDay(clientId, dayKey);

  toast.info(
    React.createElement(
      "span",
      null,
      params.t("studio.agenda.health.laterToast"),
      " ",
      React.createElement(
        "button",
        {
          type: "button",
          className: "btn btn-link btn-sm p-0 align-baseline",
          onClick: () => {
            clearHealthPromptDismiss(clientId);
            params.onFill(appointment);
          },
        },
        params.t("studio.agenda.health.askAgain")
      )
    ),
    { closeOnClick: false, autoClose: 8000 }
  );
}

/**
 * Soft warn before concluding when ficha/termo still pending (GAP-F2-112 polish · brainstorm #12 soft).
 * Returns true if staff may proceed.
 */
export async function confirmConcludeDespitePreattendGap(params: {
  canView: boolean;
  appointment: Appointment;
  t: TFunction;
  onFill?: (appointment: Appointment) => void;
}): Promise<boolean> {
  if (!params.canView) {
    return true;
  }

  const needsHealth = appointmentNeedsHealthRecord(params.appointment);
  const needsConsent = appointmentNeedsConsent(params.appointment);

  if (!needsHealth && !needsConsent) {
    return true;
  }

  const confirmation = await Swal.fire({
    title: params.t("studio.agenda.health.concludeWarnTitle"),
    text: needsConsent && !needsHealth
      ? params.t("studio.agenda.health.concludeWarnBodyConsent")
      : params.t("studio.agenda.health.concludeWarnBody"),
    icon: "warning",
    showDenyButton: Boolean(params.onFill),
    showCancelButton: true,
    confirmButtonText: params.t("studio.agenda.health.concludeWarnContinue"),
    denyButtonText: params.t("studio.agenda.health.concludeWarnFill"),
    cancelButtonText: params.t("studio.agenda.health.concludeWarnCancel"),
    confirmButtonColor: "#f7b84b",
    denyButtonColor: "#0ab39c",
    cancelButtonColor: "#878a99",
    reverseButtons: true,
  });

  if (confirmation.isDenied && params.onFill) {
    params.onFill(params.appointment);
    return false;
  }

  return confirmation.isConfirmed;
}

export { appointmentNeedsHealthRecord, appointmentNeedsConsent };
